/*! Copyright (c) Safe As Milk. All rights reserved. */
import { formatMoney } from "utils";

class VariantSelects extends HTMLElement {
    connectedCallback() {
        this.productAvailable = this.getVariantData().some(v => v.available);
        this.uniqueId = this.dataset.card || this.dataset.section;
        this.addEventListener("change", this.onVariantChange);
    }

    disconnectedCallback() {
        this.removeEventListener("change", this.onVariantChange);
    }

    onVariantChange() {
        this.updateOptions();
        this.updateMasterId();
        this.updatePickupAvailability();
        this.removeErrorMessage();

        if (!this.currentVariant) {
            this.toggleAddButton(true, "");
            this.setUnavailable();
        } else {
            this.updateURL();
            this.updateVariantInput();
            this.renderProductInfo();
            this.updateLabelsBlock();
        }

        this.dispatchEvent(new CustomEvent("on:variant:change", {
            detail: { variant: this.currentVariant }
        }));
    }

    updateOptions() {
        this.options = Array.from(this.querySelectorAll("select"), s => s.value);
    }

    updateMasterId() {
        this.currentVariant = this.getVariantData().find(variant =>
            !variant.options.map((opt, i) => this.options[i] === opt).includes(false)
        );
    }

    updateURL() {
        if (!this.currentVariant || this.dataset.updateUrl === "false") return;
        window.history.replaceState({}, "", `${this.dataset.url}?variant=${this.currentVariant.id}`);
    }

    updateVariantInput() {
        const productForms = document.querySelectorAll(
            `#product-form-${this.uniqueId}, #product-form-installment-${this.uniqueId}`
        );

        productForms.forEach(form => {
            const input = form.querySelector('input[name="id"]');
            input.value = this.currentVariant.id;
            input.dispatchEvent(new Event("change", { bubbles: true }));
        });

        const productSingle = this.closest("product-single");
        if (productSingle) productSingle.variant = this.currentVariant;
    }

    updatePickupAvailability() {
        const pickup = document.querySelector("pickup-availability");
        if (pickup) pickup.fetchAvailability(this.currentVariant.id);
    }

    removeErrorMessage() {
        this.closest("section")?.querySelector(".qty-error")?.remove();
    }

    renderProductInfo() {
        const priceContainer = document.getElementById(`price-${this.uniqueId}`);
        if (priceContainer) {
            const priceHTML = `
                <span class="u-hidden-visually">${window.theme.localize("REGULAR_PRICE")}</span>
                <span class="price__number ${this.currentVariant.compare_at_price > this.currentVariant.price ? "price__number--sale" : ""}">
                    <span class="money">
                        ${formatMoney(this.currentVariant.price, window.theme.money_product_price_format)}
                    </span>
                </span>
                ${this.currentVariant.compare_at_price > this.currentVariant.price ? `
                    <span class="u-hidden-visually">${window.theme.localize("SALE_PRICE")}</span>
                    <s class="price__compare">
                        <span class="money">
                            ${formatMoney(this.currentVariant.compare_at_price, window.theme.money_format)}
                        </span>
                    </s>
                ` : ""}
            `;
            priceContainer.innerHTML = priceHTML;
        }

        const unitPriceContainer = document.getElementById(`unit-price-${this.uniqueId}`);
        if (unitPriceContainer) {
            const unitPrice = this.currentVariant.unit_price ? `
                <span class="u-hidden-visually">${window.theme.localize("UNIT_PRICE_LABEL")}</span>
                <span class="money">
                    ${formatMoney(this.currentVariant.unit_price, window.theme.money_format)}
                </span>
            ` : "";
            unitPriceContainer.innerHTML = unitPrice;
        }

        const labelContainer = document.getElementById(`label-${this.uniqueId}`);
        if (labelContainer) {
            if (this.currentVariant.compare_at_price > this.currentVariant.price) {
                labelContainer.innerHTML = `<div class="label"><div class="label__text">${window.theme.localize("DISCOUNT_TEXT")}</div></div>`;
            } else {
                labelContainer.innerHTML = "";
            }
        }

        /* =========================================================
           INVENTORY NOTICE (FINAL CLEAN VERSION)
        ========================================================= */

        const inventoryNoticeContainer = document.getElementById(`inventory-notice-${this.uniqueId}`);

        if (inventoryNoticeContainer) {
            const wrapper = inventoryNoticeContainer.querySelector(".stock-note");
            const textEl = inventoryNoticeContainer.querySelector(".stock-note__text");

            if (!wrapper || !textEl) return;

            // RESET STATE (important for variant switching)
            wrapper.style.display = "";
            wrapper.classList.remove("stock-note--sold", "stock-note--in-stock", "stock-note--low");

            const showQty = inventoryNoticeContainer.dataset.showQty === "true";
            const limit = Number(inventoryNoticeContainer.dataset.inventoryLimit || 0);

            const qtyInput = document.getElementById(
                `quantity-${this.uniqueId}-${this.currentVariant.id}`
            );

            const quantity = qtyInput ? Number(qtyInput.dataset.qty || 0) : 0;

            const soldOut = !this.currentVariant.available;
            const continueSelling = this.currentVariant.available && quantity <= 0;
            const shopifyManaged = this.currentVariant.inventory_management === "shopify";

            const variantDataEl = document.getElementById("variant-stock-data");
            const data = variantDataEl ? JSON.parse(variantDataEl.textContent || "{}") : {};
            const hide = !!data?.[this.currentVariant.id];

            // METAFIELD HIDE OVERRIDE
            if (hide) {
                wrapper.style.display = "none";
                return;
            }

            // SOLD OUT
            if (soldOut) {
                wrapper.classList.add("stock-note--sold");
                textEl.innerHTML = window.theme.localize("QTY_NOTICE_SOLD_OUT");
                return;
            }

            // STOCK WITH QUANTITY
            if (shopifyManaged && showQty) {
                wrapper.classList.add("stock-note--in-stock");

                if (!continueSelling && quantity > 0) {
                    if (quantity <= limit) {
                        wrapper.classList.add("stock-note--low");
                        textEl.innerHTML = window.theme.localize(
                            "QTY_NOTICE_NUMBER_LOW_STOCK_HTML",
                            quantity
                        );
                    } else {
                        textEl.innerHTML = window.theme.localize(
                            "QTY_NOTICE_NUMBER_IN_STOCK_HTML",
                            quantity
                        );
                    }
                } else if (continueSelling) {
                    textEl.innerHTML = window.theme.localize("QTY_NOTICE_CONTINUE_SELLING");
                } else {
                    textEl.innerHTML = window.theme.localize("QTY_NOTICE_IN_STOCK");
                }

                return;
            }

            // DEFAULT STOCK
            wrapper.classList.add("stock-note--in-stock");
            textEl.innerHTML = window.theme.localize("QTY_NOTICE_IN_STOCK");
        }

        const skuContainer = document.getElementById(`sku-${this.uniqueId}`);
        if (skuContainer) {
            const skuHTML = this.currentVariant.sku ? `
                <div class="product-form__swatch__title u-small">
                    <span class="product-form__swatch__sub-title">
                        ${this.currentVariant.sku}
                    </span>
                </div>
            ` : "";
            skuContainer.innerHTML = skuHTML;
        }

        this.toggleAddButton(!this.currentVariant.available, window.theme.localize("SOLD_OUT"));
    }

    updateLabelsBlock() {
        const soldOutLabel = document.getElementById(`label-${this.uniqueId}-sold_out`);
        if (soldOutLabel) {
            this.currentVariant.available
                ? soldOutLabel.setAttribute("hidden", "")
                : soldOutLabel.removeAttribute("hidden");
        }
    }

    toggleAddButton(disable = true, text = "") {
        const form = document.getElementById(`product-form-${this.uniqueId}`);
        if (!form) return;

        const button = form.querySelector('[name="add"]');
        const textEl = form.querySelector('[name="add"] staged-action-text');

        if (!button) return;

        if (disable) {
            button.setAttribute("disabled", "disabled");
            if (text) textEl.textContent = text;
        } else {
            button.removeAttribute("disabled");
            textEl.textContent = window.theme.localize("ADD_TO_CART");
        }
    }

    setUnavailable() {
        const form = document.getElementById(`product-form-${this.uniqueId}`);
        const buttonText = form?.querySelector('[name="add"] > span');
        const price = document.getElementById(`price-${this.uniqueId}`);

        if (buttonText) buttonText.textContent = window.theme.localize("UNAVAILABLE");
        if (price) price.classList.add("visibility-hidden");
    }

    getVariantData() {
        this.variantData = this.variantData ||
            JSON.parse(this.querySelector('[type="application/json"]').textContent);
        return this.variantData;
    }
}

customElements.define("variant-selects", VariantSelects);

class VariantRadios extends VariantSelects {
    updateOptions() {
        const fieldsets = Array.from(this.querySelectorAll("fieldset"));
        this.options = fieldsets.map(fs =>
            Array.from(fs.querySelectorAll("input")).find(r => r.checked).value
        );
    }
}

customElements.define("variant-radios", VariantRadios);

class VariantSwatches extends HTMLElement {
    connectedCallback() {
        this.currentSelection = this.querySelector("input:checked").value;
    }
}

customElements.define("variant-swatches", VariantSwatches);

class VariantMixedInputs extends VariantSelects {
    updateOptions() {
        this.options = Array.from(this.querySelectorAll("input:checked, select"), el => el.value);
    }
}

customElements.define("variant-mixed-inputs", VariantMixedInputs);